import { ResultType } from '@williamhill/rosi-ui-shared/src/types';
import React, { Fragment, useState } from 'react';
import {
  EventNameSpan,
  iconsByResultType,
  injuryProtectionIconProps,
  Leg,
  LegSection,
  MarketNameContainer,
  SelectionNameContainer,
  struckThroughStyles
} from './legs-component';
import SGPLegsComponent from './sgp-legs-component';
import styled, { css } from 'styled-components';
import dayjs from 'dayjs';
import sortBy from 'lodash/sortBy';
import { formatHandicap, removePipes, withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { DownChevron, UpChevron, SGP } from '@williamhill/rosi-ui-shared/src/assets/icons';
import { InjuryProtection as InjuryProtectionIcon } from '@williamhill/rosi-ui-shared/src/assets/icons';
import { EventNamesContainer, LegsToggleContainer, SGPWrapper } from '.';
import {
  Block,
  DisplayHeading,
  Text,
  robotoFonts
} from '@williamhill/rosi-ui-shared/src/components/typography/Typography';
import { PriceComponent } from './price-component';
import { BetLeg } from '../../model';

type SuperParlayLegsComponentProps = {
  legs: Leg[];
  betLegs: BetLeg[];
};

type SuperParlayLegProps = {
  legs: Leg[];
  betLeg: BetLeg;
};

function SuperParlayLeg({ legs, betLeg }: SuperParlayLegProps) {
  const [legsVisible, setLegsVisible] = useState(false);

  const superParlayLegs = (type: string) =>
    legs.filter(leg => {
      return betLeg.type === type && betLeg.legRefs.includes(leg.id);
    });

  const result = getSGPResult(legs, betLeg);

  return (
    <Fragment>
      <hr />
      {superParlayLegs('leg-group').length > 0 && (
        <BetSection icon={iconsByResultType[result]}>
          <Header>
            <SGPHeader as="header">
              {superParlayLegs('leg-group').length} LEG SAME GAME PARLAY
              <SGPWrapper isSuperParlay>
                <SGP width="2rem" border={false} />
              </SGPWrapper>
              <PriceComponent
                price={betLeg.price}
                originalPrice={betLeg.originalPrice}
                isVoided={Boolean(result && result === 'void')}
              />
            </SGPHeader>
          </Header>
          <main>
            <BetSummary>
              <EventNamesContainer>
                <EventNameSpan>
                  {dayjs(superParlayLegs('leg-group')[0].eventStartTime).format('MMM DD, h:mma')} |{' '}
                  {removePipes(superParlayLegs('leg-group')[0].event.name)}
                </EventNameSpan>
              </EventNamesContainer>
              <LegsToggleContainer data-testid="sgp-leg-toggle" onClick={() => setLegsVisible(!legsVisible)}>
                {legsVisible ? <UpChevron width="1.4rem" /> : <DownChevron width="1.4rem" />}
              </LegsToggleContainer>
            </BetSummary>
            {legsVisible && <SGPLegsComponent {...{ legs: superParlayLegs('leg-group') }} />}
          </main>
        </BetSection>
      )}
      {superParlayLegs('standard').length > 0 && (
        <StraightLegs as="ul" weight="medium">
          {sortBy(superParlayLegs('standard'), ({ eventStartTime }) => new Date(eventStartTime).getTime()).map(
            ({ id, event, eventStartTime, market, selection, result, price, marketLine }) => {
              const withTestId = withRootTestId(`bet-tracker-bets-ticket-leg-${id}`);
              const selectionName = removePipes(selection.name);
              const isInjuryProtected = result?.type === 'injury_protection';
              const isVoid = result?.type === 'void';
              const isStruckThrough = isVoid || isInjuryProtected;

              return (
                <LegItem data-testid={withTestId()} icon={result && iconsByResultType[result.type]} key={id}>
                  <LegSection>
                    <header>
                      <SelectionNameContainer data-testid={withTestId('name')} isStruckThrough={isStruckThrough}>
                        {selectionName}
                        {marketLine && ` ${formatHandicap(marketLine, selectionName, false)}`}
                      </SelectionNameContainer>
                      <PriceComponent price={price} isVoided={isVoid || isInjuryProtected} />
                    </header>
                    <main>
                      <MarketNameContainer data-testid={withTestId('market')} isStruckThrough={isStruckThrough}>
                        {removePipes(market.name)}
                      </MarketNameContainer>
                      {isInjuryProtected && <InjuryProtectionIcon {...injuryProtectionIconProps} />}
                      <EventDateAndName isStruckThrough={isStruckThrough}>
                        {dayjs(eventStartTime).format('MMM DD, h:mma')} |{' '}
                        <EventNameSpan data-testid={withTestId('event-name')}>{removePipes(event.name)}</EventNameSpan>
                      </EventDateAndName>
                    </main>
                  </LegSection>
                </LegItem>
              );
            }
          )}
        </StraightLegs>
      )}
    </Fragment>
  );
}

export default function SuperParlayLegsComponent({ legs, betLegs }: SuperParlayLegsComponentProps) {
  return (
    <>
      {betLegs.map((betLeg, i) => {
        return <SuperParlayLeg key={i} betLeg={betLeg} legs={legs} />;
      })}
    </>
  );
}

const getSGPResult = (legs: Leg[], betLeg: BetLeg) => {
  const sgpLegs = legs
    .filter(leg => betLeg.type === 'leg-group' && betLeg.legRefs.includes(leg.id))
    .map(leg => leg.result && leg.result.type);

  if (sgpLegs.includes(null)) {
    return ResultType.Unresulted;
  } else if (
    sgpLegs.every(
      resultType => resultType && [ResultType.Void, ResultType.InjuryProtection].includes(resultType as ResultType)
    )
  ) {
    return ResultType.Void;
  } else if (sgpLegs.includes(ResultType.Lost)) {
    return ResultType.Lost;
  }
  return ResultType.Won;
};

const StraightLegs = styled(Text)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const LegItem = styled.li<{ icon?: { color: string; content: string } }>`
  position: relative;
  & > section {
    margin-left: 2.2rem;
  }

  ${({ icon, theme }) => {
    if (icon) {
      return css`
        &:before {
          display: flex;
          justify-content: center;
          background-color: ${theme.colors.modals[icon.color]};
          color: ${theme.colors.white};
          /* prettier-ignore */
          content: "${icon.content}";
          position: absolute;
          padding: 0.1rem;
          border-radius: 50%;
          font-size: 1rem;
          margin-top: 0.3rem;
          height: 1.3rem;
          width: 1.3rem;
        }
      `;
    }

    return css`
      &:before {
        display: flex;
        justify-content: center;
        background-color: ${theme.colors.modals.neutral};
        /* prettier-ignore */
        content: "";
        position: absolute;
        padding: 0.1rem;
        border-radius: 50%;
        font-size: 1rem;
        margin-top: 0.3rem;
        height: 1.3rem;
        width: 1.3rem;
      }
    `;
  }};
`;

const BetSection = styled.section<{ icon?: { color: string; content: string } }>`
  background-color: ${({ theme }) => theme.colors.white};
  position: relative;

  ${({ icon, theme }) => {
    if (icon) {
      return css`
        &:before {
          display: flex;
          justify-content: center;
          background-color: ${theme.colors.modals[icon.color]};
          color: ${theme.colors.white};
          /* prettier-ignore */
          content: "${icon.content}";
          position: absolute;
          padding: 0.1rem;
          border-radius: 50%;
          font-size: 1rem;
          margin-top: 0.6rem;
          height: 1.3rem;
          width: 1.3rem;
        }
      `;
    }

    return css`
      &:before {
        display: flex;
        justify-content: center;
        background-color: ${theme.colors.modals.neutral};
        /* prettier-ignore */
        content: "";
        position: absolute;
        padding: 0.1rem;
        border-radius: 50%;
        font-size: 1rem;
        margin-top: 0.6rem;
        height: 1.3rem;
        width: 1.3rem;
      }
    `;
  }};

  & > header {
    font-weight: 700;
  }

  & hr {
    border-style: none;
    border-top: 1px solid ${({ theme }) => theme.colors.grey200};
    height: 1px;
    margin: 0.8rem 0;
  }
`;

const BetSummary = styled.div`
  color: ${({ theme }) => theme.colors.brand.quaternary};
  display: flex;
  font-size: ${robotoFonts.sm};
  padding-left: 2.2rem;
  margin-top: 0.3rem;
`;

const SGPHeader = styled(DisplayHeading).attrs({ as: 'header', size: 'xs', weight: 'bold' })`
  display: flex;
  align-items: center;
`;

const Header = styled.header`
  margin-left: 2.2rem;
`;

const EventDateAndName = styled(Block).attrs({ weight: 'regular', size: 'sm' })<{ isStruckThrough?: boolean }>`
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles}
`;

export const forTestsOnly = {
  getSGPResult
};
